// File: client/src/components/rules/nodeConfigs/ConditionalConfig.js
import React, { useEffect } from 'react';
import { Form, Tabs, Input, Select } from 'antd';
import JavaScriptExpressionEditor from '../expressionEditor/JavaScriptExpressionEditor';
import SimpleConditionalEditor from '../expressionEditor/SimpleConditionalEditor';

const { Option } = Select;
const { TabPane } = Tabs;

const ConditionalConfig = () => {
  const form = Form.useFormInstance();
  const configurationType = Form.useWatch('type', form) || 'simple';

  // In useEffect when type changes
  useEffect(() => {
    // Set default values based on configuration type
    if (configurationType === 'javascript' && !form.getFieldValue('condition')) {
      form.setFieldValue('condition', 'record');
    } else if (configurationType === 'simple') {
      if (!form.getFieldValue('simpleCondition')) {
        // Use just the field name for the simple version
        form.setFieldValue('simpleCondition', 'record.callType === "inbound"');
      }
    } else if (configurationType === 'ai') {
      if (!form.getFieldValue('prompt')) {
        form.setFieldValue('prompt', 'Is this call a high-priority issue based on the conversation?');
      }
      if (!form.getFieldValue('options')) {
        form.setFieldValue('options', 'true,false');
      }
    }
  }, [configurationType, form]);

  // Handle JavaScript condition change
  const handleConditionChange = (value) => {
    form.setFieldsValue({ condition: value });
  };

  // Handle Simple condition change
  const handleSimpleConditionChange = (value) => {
    form.setFieldsValue({ simpleCondition: value });
  };

  // Handle tab change
  const handleTabChange = (activeKey) => {
    form.setFieldValue('type', activeKey);
  };

  // Add global styles for tooltip icons
  useEffect(() => {
    // Add a style tag to modify tooltip icons
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `
      .ant-form-item-tooltip {
        color: #1890ff !important;
        margin-left: 8px !important;
      }
    `;
    document.head.appendChild(styleTag);

    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  return (
    <div>
      <Form.Item name="type" hidden={true}>
        <Input />
      </Form.Item>

      <Tabs activeKey={configurationType} onChange={handleTabChange}>
        <TabPane tab="Simple" key="simple">
          <Form.Item
            name="simpleCondition"
            tooltip={{ title: "Build conditional logic visually" }}
            rules={[{ required: true, message: 'Please define a condition' }]}
          >
            <SimpleConditionalEditor
              value={form.getFieldValue('simpleCondition')}
              onChange={handleSimpleConditionChange}
              evaluateOnChange={true}
            />
          </Form.Item>
        </TabPane>

        <TabPane tab="JavaScript" key="javascript">
          <Form.Item
            name="condition"
            label="Condition"
            tooltip={{ title: "JavaScript condition that evaluates to true or false" }}
            rules={[{ required: true, message: 'Please enter a condition' }]}
          >
            <JavaScriptExpressionEditor
              value={form.getFieldValue('condition')}
              onChange={handleConditionChange}
              multiline={true}
              height={120}
              placeholder="record"
              evaluateOnChange={true}
            />
          </Form.Item>
        </TabPane>

        <TabPane tab="AI" key="ai">
          <Form.Item
            name="prompt"
            label="AI Prompt"
            tooltip={{ title: "Natural language prompt to evaluate the call" }}
            rules={[{ required: true, message: 'Please enter a prompt' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="e.g., Determine if this call requires supervisor review based on customer sentiment"
            />
          </Form.Item>

          <Form.Item
            name="options"
            label="Options"
            tooltip={{ title: "Possible values that AI should respond with" }}
          >
            <Input placeholder="true,false (comma-separated options)" />
          </Form.Item>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default ConditionalConfig;
